import React, { useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import {
    Heart, Share2, Edit3, Trash2, Loader, Disc3, FileText, Calendar, Users,
    Lock, DollarSign, LogIn
} from 'lucide-react';
import MediaFeedCard from '../components/MediaFeedCard';
import ShareModal from '../components/ShareModal';
import SignupInviteModal from '../components/SignupInviteModal';
import AccessRequiredModal from '../components/AccessRequiredModal';
import AlbumFormModal from '../components/AlbumFormModal';
import MusicUploadModal from '../components/MusicUploadModal';
import VideoUploadModal from '../components/VideoUploadModal';
import ImageUploadModal from '../components/ImageUploadModal';
import ContributingArtistsDisplay from '../components/ContributingArtistsDisplay';
import { useAuth } from '../context/AuthContext';
import api from '../services/api';

const ITEMS_LIMIT = 20;

const AlbumDisplay = () => {
    // :identifier is the album's URL slug (e.g. "The-Nectar-of-Life"), or a
    // legacy numeric id for old links — only used to resolve the album via
    // the view endpoint below. Every other call uses the resolved album.id.
    const { username, identifier } = useParams();
    const { user, isAuthenticated, hasActiveSubscription } = useAuth();

    const [album, setAlbum] = useState(null);
    const [accessRequired, setAccessRequired] = useState(false);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [deleting, setDeleting] = useState(false);

    const [items, setItems] = useState([]);
    const [itemsTotal, setItemsTotal] = useState(0);
    const [itemsLoading, setItemsLoading] = useState(false);
    const [loadingMore, setLoadingMore] = useState(false);

    const [passwordInput, setPasswordInput] = useState('');
    const [passwordError, setPasswordError] = useState('');
    const [passwordLoading, setPasswordLoading] = useState(false);

    const [shareModal, setShareModal] = useState(false);
    const [signupInvite, setSignupInvite] = useState({ isOpen: false, contentType: 'content' });
    const [itemAccessModal, setItemAccessModal] = useState({ isOpen: false, item: null });
    const [showEditModal, setShowEditModal] = useState(false);
    // The album item currently open for full editing, or null when closed —
    // only ever set for items the viewer actually uploaded themselves.
    const [editingMediaItem, setEditingMediaItem] = useState(null);

    const loadAlbum = async () => {
        try {
            setLoading(true);
            setError(null);
            const response = await api.get(`/albums/view/${username}/${identifier}`);
            if (response.data.success) {
                const data = response.data.data;
                setAlbum(data);
                setAccessRequired(!!data.access_required);
            } else {
                setError('Album not found.');
            }
        } catch (err) {
            console.error('Error loading album:', err);
            if (err.response?.status === 404) {
                setError('Album not found.');
            } else {
                setError('Failed to load album.');
            }
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        loadAlbum();
    }, [username, identifier]);

    const isOwner = isAuthenticated && user?.profile?.username === username;
    const isHardGate = accessRequired &&
        (album?.access_type === 'private' || album?.access_type === 'password' || album?.access_type === 'payment');
    const isSoftGated = accessRequired && !isHardGate;

    const loadItems = async () => {
        if (!album) return;
        try {
            setItemsLoading(true);
            const response = await api.get(`/albums/${album.id}/items`, { params: { limit: ITEMS_LIMIT, offset: 0 } });
            if (response.data.success) {
                setItems(response.data.data || []);
                setItemsTotal(response.data.total || 0);
            }
        } catch (err) {
            console.error('Error loading album items:', err);
        } finally {
            setItemsLoading(false);
        }
    };

    // Load the tagged media once the album is unlocked (not hard-gated)
    useEffect(() => {
        if (!album || isHardGate) return;
        loadItems();
    }, [album, isHardGate]);

    const loadMoreItems = async () => {
        try {
            setLoadingMore(true);
            const response = await api.get(`/albums/${album.id}/items`, { params: { limit: ITEMS_LIMIT, offset: items.length } });
            if (response.data.success) {
                setItems(prev => [...prev, ...(response.data.data || [])]);
            }
        } catch (err) {
            console.error('Error loading more album items:', err);
        } finally {
            setLoadingMore(false);
        }
    };

    const handleLike = async () => {
        if (!isAuthenticated) {
            setSignupInvite({ isOpen: true, contentType: 'album' });
            return;
        }
        try {
            const response = await api.post(`/albums/${album.id}/like`);
            if (response.data.success) {
                setAlbum(prev => ({
                    ...prev,
                    like_count: response.data.data.likeCount,
                    user_has_liked: response.data.data.liked
                }));
            }
        } catch (err) {
            console.error('Error toggling album like:', err);
        }
    };

    const handleDelete = async () => {
        if (!window.confirm(`Delete the album "${album.title}"? This cannot be undone.`)) return;
        setDeleting(true);
        try {
            await api.delete(`/albums/${album.id}`);
            window.location.href = `/profile/${username}`;
        } catch (err) {
            console.error('Error deleting album:', err);
            setDeleting(false);
        }
    };

    const handlePasswordSubmit = async (e) => {
        e.preventDefault();
        if (!passwordInput.trim()) return;
        setPasswordLoading(true);
        setPasswordError('');
        try {
            const response = await api.post(`/albums/${album.id}/verify-password`, { password: passwordInput });
            if (response.data.success) {
                setPasswordInput('');
                loadAlbum();
            }
        } catch (err) {
            if (err.response?.status === 401) {
                setPasswordError('Incorrect password. Please try again.');
            } else {
                setPasswordError('Something went wrong. Please try again.');
            }
        } finally {
            setPasswordLoading(false);
        }
    };

    const hasMore = items.length < itemsTotal;

    if (loading) {
        return (
            <div className="min-h-screen bg-gray-900 flex items-center justify-center">
                <Loader className="w-8 h-8 animate-spin text-purple-500" />
            </div>
        );
    }

    if (error || !album) {
        return (
            <div className="min-h-screen bg-gray-900 flex items-center justify-center text-center px-4">
                <div>
                    <p className="text-gray-400 mb-4">{error || 'Album not found.'}</p>
                    <Link to="/" className="text-purple-400 hover:text-purple-300">Back to Home</Link>
                </div>
            </div>
        );
    }

    return (
        <div className="min-h-screen bg-gray-900 text-white">
            {/* Header — same action-bar pattern as MediaDisplay.jsx */}
            <div className="bg-gray-800/50 backdrop-blur-sm border-b border-gray-700 px-0 sm:px-6 py-4">
                <div className="max-w-6xl mx-auto px-2 sm:px-0">
                    <div className="flex items-center flex-wrap gap-2 sm:gap-4">
                        <button
                            onClick={() => setShareModal(true)}
                            className="flex items-center gap-2 px-4 py-2 bg-gray-700 hover:bg-gray-600 rounded-lg transition-colors"
                        >
                            <Share2 className="w-4 h-4" />
                            Share
                        </button>
                        <button
                            onClick={handleLike}
                            className={`flex items-center gap-2 px-3 py-2 rounded-lg transition-colors ${
                                album.user_has_liked
                                    ? 'text-red-400 bg-red-400/10 hover:bg-red-400/20'
                                    : 'text-gray-400 hover:text-red-400 hover:bg-red-400/10'
                            }`}
                            title={isAuthenticated
                                ? (album.user_has_liked ? 'Unlike this album' : 'Like this album')
                                : 'Sign up to like albums and support artists'
                            }
                        >
                            <Heart className={`w-4 h-4 ${album.user_has_liked ? 'fill-current' : ''}`} />
                            {album.like_count || 0}
                        </button>

                        {isOwner && (
                            <>
                                <button
                                    onClick={() => setShowEditModal(true)}
                                    className="flex items-center gap-2 px-4 py-2 bg-blue-600 hover:bg-blue-700 rounded-lg transition-colors"
                                >
                                    <Edit3 className="w-4 h-4" />
                                    Edit
                                </button>
                                <button
                                    onClick={handleDelete}
                                    disabled={deleting}
                                    className="flex items-center gap-2 px-4 py-2 bg-red-600 hover:bg-red-700 rounded-lg transition-colors disabled:opacity-50"
                                >
                                    {deleting ? <Loader className="w-4 h-4 animate-spin" /> : <Trash2 className="w-4 h-4" />}
                                    Delete
                                </button>
                            </>
                        )}
                    </div>
                </div>
            </div>

            {/* Main Content — same max-w-6xl container as MediaDisplay.jsx */}
            <div className="max-w-6xl mx-auto px-0 sm:px-6 py-6">
                <div className="mb-8 relative">
                    {isHardGate ? (
                        <div className="relative">
                            {album.cover_image_url ? (
                                <img
                                    src={album.cover_image_url}
                                    alt={album.title}
                                    className="w-full object-cover rounded-lg shadow-2xl"
                                    style={{ minHeight: '320px', maxHeight: '70vh' }}
                                />
                            ) : (
                                <div className="bg-gradient-to-br from-purple-900 to-pink-900 rounded-lg overflow-hidden flex items-center justify-center" style={{ minHeight: '320px' }}>
                                    <Disc3 className="w-24 h-24 text-white/20" />
                                </div>
                            )}
                            <div className="absolute inset-0 flex items-center justify-center z-10 p-6 rounded-lg">
                                <div className="absolute inset-0 bg-black/25 rounded-lg" />
                                <div className="relative z-10 w-full max-w-md">
                                    {album.access_type === 'private' ? (
                                        <div className="bg-gray-900/95 border border-blue-700/40 rounded-2xl p-8 text-center shadow-2xl">
                                            <div className="w-16 h-16 bg-blue-500/20 rounded-full flex items-center justify-center mx-auto mb-5">
                                                <Users className="w-8 h-8 text-blue-400" />
                                            </div>
                                            <h2 className="text-2xl font-bold text-white mb-3">Private Album</h2>
                                            <p className="text-gray-400 text-sm leading-relaxed">This album is kept private by its owner.</p>
                                        </div>
                                    ) : album.access_type === 'payment' ? (
                                        <div className="bg-gray-900/95 border border-green-700/40 rounded-2xl p-8 text-center shadow-2xl">
                                            <div className="w-16 h-16 bg-green-500/20 rounded-full flex items-center justify-center mx-auto mb-5">
                                                <DollarSign className="w-8 h-8 text-green-400" />
                                            </div>
                                            <h2 className="text-2xl font-bold text-white mb-2">Support the Artist</h2>
                                            {album.price && (
                                                <p className="text-3xl font-bold text-green-400 mb-2">
                                                    {album.currency || 'USD'} {parseFloat(album.price).toFixed(2)}
                                                </p>
                                            )}
                                            <p className="text-gray-400 text-sm leading-relaxed">Unlock this album with a one-time purchase.</p>
                                        </div>
                                    ) : (
                                        <div className="bg-gray-900/95 border border-yellow-700/40 rounded-2xl p-8 text-center shadow-2xl">
                                            <div className="w-16 h-16 bg-yellow-500/20 rounded-full flex items-center justify-center mx-auto mb-5">
                                                <Lock className="w-8 h-8 text-yellow-400" />
                                            </div>
                                            <h2 className="text-2xl font-bold text-white mb-3">Password Protected</h2>
                                            {album.access_hint && (
                                                <p className="text-gray-500 text-xs mb-4">Hint: {album.access_hint}</p>
                                            )}
                                            <form onSubmit={handlePasswordSubmit} className="flex flex-col gap-3">
                                                <input
                                                    type="password"
                                                    value={passwordInput}
                                                    onChange={(e) => setPasswordInput(e.target.value)}
                                                    placeholder="Enter password"
                                                    className="w-full px-3 py-2.5 bg-gray-800 border border-gray-700 rounded-lg text-white text-center focus:ring-2 focus:ring-purple-500 focus:border-transparent"
                                                    autoFocus
                                                />
                                                {passwordError && <p className="text-red-400 text-sm">{passwordError}</p>}
                                                <button
                                                    type="submit"
                                                    disabled={passwordLoading || !passwordInput.trim()}
                                                    className="w-full py-3 bg-yellow-600 hover:bg-yellow-700 text-white font-semibold rounded-lg transition-colors disabled:opacity-50 flex items-center justify-center gap-2"
                                                >
                                                    {passwordLoading ? <Loader className="w-4 h-4 animate-spin" /> : <LogIn className="w-4 h-4" />}
                                                    Unlock
                                                </button>
                                            </form>
                                        </div>
                                    )}
                                </div>
                            </div>
                        </div>
                    ) : (
                        <>
                            {/* Cover image — same treatment as renderMedia()'s audio case in
                                MediaDisplay.jsx: gradient-framed card with a centered,
                                aspect-square cover, not a full-width banner (that style is
                                only used for the gated backdrop above). */}
                            <div className="bg-gradient-to-br from-purple-600 to-pink-600 rounded-lg p-6 text-center mb-8">
                                <div className="bg-gray-900/40 backdrop-blur-sm rounded-lg p-4 max-w-md lg:max-w-2xl mx-auto">
                                    {album.cover_image_url ? (
                                        <div className="relative group">
                                            <img
                                                src={album.cover_image_url}
                                                alt={`${album.title} cover art`}
                                                className="w-full aspect-square object-cover rounded-lg shadow-lg"
                                                onContextMenu={(e) => e.preventDefault()}
                                            />
                                        </div>
                                    ) : (
                                        <div className="relative w-full aspect-square bg-gradient-to-br from-gray-800 to-gray-900 rounded-lg flex flex-col items-center justify-center text-gray-400">
                                            <Disc3 className="w-16 h-16 mb-3 text-white/30" />
                                            <h3 className="font-bold text-lg text-white mb-1">{album.title}</h3>
                                            <p className="text-sm">by {album.display_name || album.username}</p>
                                        </div>
                                    )}
                                </div>
                            </div>

                            {/* Media Info — same outer card as MediaDisplay.jsx, wrapping
                                both the Main Info column and the Details sidebar. */}
                            <div className="bg-gray-800/50 rounded-lg p-6">
                            <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
                                {/* Main Info */}
                                <div className="lg:col-span-2">
                                    <h1 className="text-3xl font-bold mb-2">{album.title}</h1>
                                    {!!album.is_copyrighted && album.copyright_text && (
                                        <p className="text-xs text-gray-500 mb-2">{album.copyright_text}</p>
                                    )}
                                    {isSoftGated && (
                                        <span className="inline-flex items-center gap-1.5 mb-4 px-3 py-1 rounded-full text-xs font-medium bg-purple-500/15 text-purple-300 border border-purple-500/30">
                                            <Lock className="w-3 h-3" />
                                            {album.access_type === 'login' ? 'Must be logged in' : 'Supporters only'}
                                        </span>
                                    )}
                                    {!isSoftGated && <div className="mb-4" />}

                                    <div className="mb-6">
                                        <div className="flex items-center gap-2 mb-3 pb-2 border-b border-gray-700 text-sm font-semibold text-purple-400">
                                            <FileText className="w-4 h-4" />
                                            Description
                                        </div>
                                        {album.description ? (
                                            <p className="text-gray-400 whitespace-pre-wrap leading-relaxed">{album.description}</p>
                                        ) : (
                                            <p className="text-gray-500 italic">No description</p>
                                        )}
                                    </div>

                                    {/* Creator Info — same card as MediaDisplay.jsx */}
                                    <div className="flex items-center gap-4 p-4 bg-gray-700/30 rounded-lg">
                                        {album.profile_picture_url ? (
                                            <img src={album.profile_picture_url} alt={album.display_name || album.username} width="64" height="64" className="w-16 h-16 rounded-full object-cover" />
                                        ) : (
                                            <div className="w-16 h-16 bg-gradient-to-r from-purple-500 to-pink-500 rounded-full flex items-center justify-center">
                                                <span className="text-white font-semibold text-xl">{album.display_name?.charAt(0) || album.username?.charAt(0) || 'U'}</span>
                                            </div>
                                        )}
                                        <div>
                                            <p className="font-semibold text-lg">{album.display_name || album.username}</p>
                                            <p className="text-gray-400">@{album.username}</p>
                                            <Link to={`/profile/${album.username}`} className="text-purple-400 hover:text-purple-300 text-sm">View Profile →</Link>
                                        </div>
                                    </div>
                                </div>

                                {/* Sidebar — same "Details" card as MediaDisplay.jsx */}
                                <div className="bg-gray-700/30 rounded-lg p-4">
                                    <h3 className="font-semibold mb-4 text-gray-300 flex items-center gap-2">
                                        <FileText className="w-5 h-5" /> Details
                                    </h3>
                                    <div className="space-y-3 text-sm">
                                        <div className="flex justify-between items-center">
                                            <span className="text-gray-400">Type:</span>
                                            <span className="font-medium">Album</span>
                                        </div>
                                        <div className="flex justify-between items-center">
                                            <span className="text-gray-400">Items:</span>
                                            <span className="font-medium">{album.item_count || 0}</span>
                                        </div>

                                        <div className="pt-3 border-t border-gray-600">
                                            <div className="flex items-center gap-2 mb-1 text-gray-400">
                                                <Calendar className="w-4 h-4" /> Created
                                            </div>
                                            <div className="text-gray-300 text-xs">
                                                {new Date(album.created_at).toLocaleDateString('en-US', { year: 'numeric', month: 'long', day: 'numeric' })}
                                            </div>
                                        </div>

                                        {album.access_level && album.access_level !== 'public' && (
                                            <div className="pt-3 border-t border-gray-600 flex justify-between items-center">
                                                <span className="text-gray-400">Access:</span>
                                                <span className="capitalize text-yellow-300 text-xs font-medium bg-yellow-500/10 px-2 py-0.5 rounded-full">
                                                    {album.access_level.replace(/_/g, ' ')}
                                                </span>
                                            </div>
                                        )}

                                        {/* Contributors */}
                                        <div className="pt-3 border-t border-gray-600">
                                            <h4 className="text-sm font-medium text-gray-300 mb-3 flex items-center gap-2">
                                                <Users className="w-4 h-4" /> Contributors
                                            </h4>
                                            <div className="flex items-center gap-3 p-2 bg-gray-700/30 rounded-lg mb-2">
                                                {album.profile_picture_url ? (
                                                    <img src={album.profile_picture_url} alt={album.display_name || album.username} width="28" height="28" className="w-7 h-7 rounded-full object-cover" />
                                                ) : (
                                                    <div className="w-7 h-7 bg-gradient-to-r from-purple-500 to-pink-500 rounded-full flex items-center justify-center">
                                                        <span className="text-white font-semibold text-xs">{album.display_name?.charAt(0) || 'U'}</span>
                                                    </div>
                                                )}
                                                <div>
                                                    <p className="font-medium text-white text-xs">{album.display_name || album.username}</p>
                                                    <p className="text-gray-400 text-xs">Creator</p>
                                                </div>
                                            </div>
                                            <ContributingArtistsDisplay
                                                mediaType="album"
                                                mediaId={album.id}
                                                showTitle={false}
                                                layout="vertical"
                                                maxVisible={null}
                                            />
                                        </div>

                                        {/* Stats */}
                                        <div className="pt-3 border-t border-gray-600 space-y-2">
                                            <div className="flex justify-between items-center text-xs">
                                                <span className="text-gray-400">Likes:</span>
                                                <span className="text-red-300 font-medium">{album.like_count || 0}</span>
                                            </div>
                                            <div className="flex justify-between items-center text-xs">
                                                <span className="text-gray-400">Views:</span>
                                                <span className="text-blue-300 font-medium">{(album.view_count || 0).toLocaleString()}</span>
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            </div>
                            </div>
                        </>
                    )}
                </div>

                {/* Album media — same max-w-4xl width as /feed and LikedMedia.jsx so
                    MediaFeedCard renders at its normal size on desktop, instead of
                    stretching to the wider max-w-6xl used by the header above.
                    px-4 sm:px-0 restores the horizontal padding MediaFeedCard's
                    own -mx-4 sm:mx-0 mobile bleed expects from its parent — the
                    outer page container above is px-0 on mobile (to match
                    MediaDisplay.jsx), so without this the cards (and their text)
                    would bleed flush to the screen edge instead of matching /feed. */}
                {!isHardGate && (
                    <div className="mt-8 max-w-4xl mx-auto px-4 sm:px-0">
                        <h3 className="text-xl font-bold text-white mb-6">
                            Media in this Album ({itemsTotal})
                        </h3>

                        {itemsLoading ? (
                            <div className="text-center py-16">
                                <Loader className="w-8 h-8 animate-spin text-purple-500 mx-auto" />
                            </div>
                        ) : items.length === 0 ? (
                            <div className="glass-card p-12 text-center">
                                <Disc3 className="w-16 h-16 text-gray-600 mx-auto mb-4" />
                                <p className="text-gray-400">No media in this album yet</p>
                            </div>
                        ) : (
                            <>
                                <div className="space-y-4 sm:space-y-8">
                                    {items.map((item) => (
                                        <MediaFeedCard
                                            key={`${item.file_type}-${item.id}`}
                                            item={{ ...item, mediaType: item.file_type }}
                                            isAuthenticated={isAuthenticated}
                                            hasActiveSubscription={hasActiveSubscription}
                                            onRequireAuth={(contentType) => setSignupInvite({ isOpen: true, contentType })}
                                            onShare={() => setShareModal(true)}
                                            onAccessRequired={(mediaItem) => setItemAccessModal({ isOpen: true, item: mediaItem })}
                                            // Only the actual uploader can edit — a tagged/contributing
                                            // artist's media can appear here too, not just the album
                                            // owner's own uploads, and only the uploader is authorized
                                            // to save changes to it (enforced backend-side too).
                                            onEdit={item.user_id === user?.id ? (mediaItem) => setEditingMediaItem(mediaItem) : undefined}
                                        />
                                    ))}
                                </div>

                                {hasMore && (
                                    <div className="mt-8 text-center">
                                        <button
                                            onClick={loadMoreItems}
                                            disabled={loadingMore}
                                            className="inline-flex items-center gap-2 px-6 py-2.5 bg-purple-600 hover:bg-purple-700 disabled:opacity-50 text-white text-sm font-medium rounded-lg transition-colors"
                                        >
                                            {loadingMore ? (
                                                <>
                                                    <Loader className="w-4 h-4 animate-spin" />
                                                    Loading...
                                                </>
                                            ) : (
                                                'Load More'
                                            )}
                                        </button>
                                    </div>
                                )}
                            </>
                        )}
                    </div>
                )}
            </div>

            <ShareModal
                isOpen={shareModal}
                onClose={() => setShareModal(false)}
                mediaTitle={album.title}
                mediaType="album"
                mediaUrl={`${window.location.origin}/album/${username}/${album.slug || album.id}`}
                username={username}
                artistName={album.display_name || album.username}
                imageUrl={album.cover_image_url}
                description={album.description}
            />

            <SignupInviteModal
                isOpen={signupInvite.isOpen}
                onClose={() => setSignupInvite({ isOpen: false, contentType: 'content' })}
                contentType={signupInvite.contentType}
            />

            <AccessRequiredModal
                isOpen={itemAccessModal.isOpen}
                onClose={() => setItemAccessModal({ isOpen: false, item: null })}
                accessLevel={itemAccessModal.item?.access_level}
                action="listen"
                isAuthenticated={isAuthenticated}
                viewUrl={itemAccessModal.item ? `/view/${itemAccessModal.item.mediaType}/${itemAccessModal.item.username}/${itemAccessModal.item.stored_filename || itemAccessModal.item.original_filename || itemAccessModal.item.id}` : null}
            />

            <AlbumFormModal
                isOpen={showEditModal}
                editingAlbum={album}
                onClose={() => setShowEditModal(false)}
                onSuccess={() => {
                    setShowEditModal(false);
                    loadAlbum();
                }}
            />

            {/* Full media editing, in place, for whichever item's Edit button
                was clicked — same modals used for fresh uploads elsewhere,
                just opened directly to the metadata step for existing media. */}
            <MusicUploadModal
                isOpen={editingMediaItem?.file_type === 'audio'}
                editingUpload={editingMediaItem}
                onClose={() => setEditingMediaItem(null)}
                onSuccess={() => { setEditingMediaItem(null); loadItems(); }}
            />
            <VideoUploadModal
                isOpen={editingMediaItem?.file_type === 'video'}
                editingUpload={editingMediaItem}
                onClose={() => setEditingMediaItem(null)}
                onSuccess={() => { setEditingMediaItem(null); loadItems(); }}
            />
            <ImageUploadModal
                isOpen={editingMediaItem?.file_type === 'image'}
                editingUpload={editingMediaItem}
                onClose={() => setEditingMediaItem(null)}
                onSuccess={() => { setEditingMediaItem(null); loadItems(); }}
            />
        </div>
    );
};

export default AlbumDisplay;
